import { useState, useRef, useEffect } from "react";
import { BlobServiceClient } from "@azure/storage-blob";
import { Axios } from "utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { GET_TASK_EXTENDED_INFO, removeUploadedFile, uploadTaskFile } from "..";

const getExtension = (filename) => filename.split(".").pop().toLowerCase();

const generateFileName = (file) => {
  const ext = getExtension(file.name);
  const random = Math.random().toString(36).substring(2, 12);
  return `${random}.${ext}`;
};

export const useAzureFileUploader = ({
  studentTaskId = null,
  supportedExtensions = [],
  courseTaskId = null,
  taskGroup = null,
}) => {
  const queryClient = useQueryClient();

  const lastProgressUpdate = useRef(new Map());
  const uploadControllers = useRef(new Map());
  const [allUploadingFiles, setAllUploadingFiles] = useState({});
  const [deletingId, setDeletingId] = useState(null);

  const uploadingFiles = allUploadingFiles[studentTaskId] || [];

  useEffect(() => {
    if (!allUploadingFiles[studentTaskId]) {
      setAllUploadingFiles((prev) => ({ ...prev, [studentTaskId]: [] }));
    }
  }, [studentTaskId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setUploadingFiles((prev) => prev.filter((file) => file.error === null));
    }, 3000);

    return () => clearTimeout(timer);
  }, [uploadingFiles?.length]);

  const setUploadingFiles = (updater) => {
    setAllUploadingFiles((prev) => ({
      ...prev,
      [studentTaskId]: updater(prev[studentTaskId] || []),
    }));
  };

  const uploadFile = async (file, type = 0) => {
    const ext = getExtension(file.name);
    const fileId = Date.now() + Math.random();
    const controller = new AbortController();

    setUploadingFiles((prev) => [
      ...prev,
      { id: fileId, name: file.name, progress: 0, error: null },
    ]);

    if (!supportedExtensions.includes(`.${ext}`)) {
      setUploadingFiles((prev) =>
        prev.map((f) =>
          f.id === fileId
            ? {
                ...f,
                error:
                  "This document is not supported, please delete and upload another file.",
              }
            : f
        )
      );
      return null;
    }

    uploadControllers.current.set(fileId, controller);
    lastProgressUpdate.current.set(fileId, 0);

    try {
      const res = await Axios.get(`v2/Storage/Storage/CreateUploadUrl/${type}`);
      const { url: uploadUrl, container } = res.data;
      const containerName = `storage/${container}`;

      const blobService = new BlobServiceClient(uploadUrl);
      const containerClient = blobService.getContainerClient(containerName);
      const fileName = generateFileName(file);
      const blobClient = containerClient.getBlockBlobClient(fileName);

      if (controller.signal.aborted)
        throw new DOMException("Upload aborted", "AbortError");

      await blobClient.uploadData(file, {
        blobHTTPHeaders: { blobContentType: file.type },
        abortSignal: controller.signal,
        concurrency: 2, // Number of chunks uploaded in parallel
        blockSize: 256 * 1024, // Chunk size in bytes (256 KB)
        onProgress: (progress) => {
          const percent = file.size
            ? Math.round((progress.loadedBytes / file.size) * 100)
            : 100;

          const now = Date.now();
          const lastUpdate = lastProgressUpdate.current.get(fileId) || 0;

          if (now - lastUpdate > 200 || percent === 100) {
            lastProgressUpdate.current.set(fileId, now);
            setUploadingFiles((prev) =>
              prev.map((f) =>
                f.id === fileId ? { ...f, progress: percent } : f
              )
            );
          }
        },
      });

      return {
        filePath: fileName,
        originalName: file.name,
      };
    } catch (error) {
      if (error.name === "AbortError") {
        setUploadingFiles((prev) =>
          prev.map((f) =>
            f.id === fileId ? { ...f, error: error.message } : f
          )
        );
      }
      return null;
    } finally {
      uploadControllers.current.delete(fileId);
      lastProgressUpdate.current.delete(fileId);
      setUploadingFiles((prev) => prev.filter((f) => f.id !== fileId));
    }
  };

  const cancelUpload = (id) => {
    const controller = uploadControllers.current.get(id);
    if (controller) {
      controller.abort();
      uploadControllers.current.delete(id);
    }
    lastProgressUpdate.current.delete(id);
    setUploadingFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const uploadStudentTaskFile = useMutation({
    mutationFn: uploadTaskFile,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          GET_TASK_EXTENDED_INFO,
          courseTaskId,
          studentTaskId,
          taskGroup,
        ],
      });
    },
    onError: (err) => {
      console.error("Failed to upload file:", err);
    },
  });

  const deleteUploadedFile = useMutation({
    mutationFn: removeUploadedFile,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          GET_TASK_EXTENDED_INFO,
          courseTaskId,
          studentTaskId,
          taskGroup,
        ],
      });
    },
    onError: (err) => {
      console.error("Failed to delete file:", err);
    },
  });

  const deleteFile = async (fileId) => {
    if (fileId && studentTaskId) {
      setDeletingId(fileId);
      deleteUploadedFile.mutate(
        { studentTaskId, fileId },
        {
          onSettled: () => setDeletingId(null),
        }
      );
    }
  };

  return {
    uploadingFiles,
    uploadFile,
    cancelUpload,
    deleteFile,
    deletingId,
    uploadStudentTaskFile,
    deleteUploadedFile,
  };
};
